import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { useWorkspaces } from '@/api/workspaces';
import {
  useCreateFolder,
  useFolders,
  useMoveFolder,
  useReorderFolder,
} from '@/api/folders';
import { FolderTree, type FolderDragData } from '@/components/folders/FolderTree';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import { apiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useIsClient } from '@/hooks/useIsClient';
import { useOrganization } from '@/api/organizations';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useToast } from '@/hooks/useToast';
import { FolderPlus } from 'lucide-react';
import type { Folder } from '@/types/api';

export function WorkspacePage() {
  const { orgId = '' } = useParams<{ orgId: string }>();
  const workspaces = useWorkspaces(orgId);
  const workspaceId = workspaces.data?.[0]?.id ?? null;
  const [showArchived, setShowArchived] = useState(false);
  const folders = useFolders(workspaceId, showArchived);
  const create = useCreateFolder();
  const reorderFolder = useReorderFolder();
  const moveFolder = useMoveFolder();
  const toast = useToast();
  const { hasRole } = useAuth();
  const isClient = useIsClient();
  const isStaff = hasRole(['sysadmin', 'manager', 'staff']);
  const canManageFolders = hasRole(['sysadmin', 'manager', 'staff', 'client_admin']);
  const org = useOrganization(isStaff ? orgId : undefined);
  usePageTitle(isClient ? 'My files' : 'Workspace');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const [creatingParent, setCreatingParent] = useState<string | null | undefined>(undefined);
  const [newName, setNewName] = useState('');

  const rootFolders = useMemo(
    () => (folders.data ?? []).filter((f) => f.parent_id === null && !f.is_archived),
    [folders.data],
  );

  const submitCreate = async () => {
    if (!workspaceId || !newName.trim()) return;
    try {
      await create.mutateAsync({
        workspace_id: workspaceId,
        parent_id: creatingParent ?? null,
        name: newName.trim(),
      });
      toast.success('Folder created');
      setCreatingParent(undefined);
      setNewName('');
    } catch (e) {
      toast.error('Create failed', apiError(e));
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || !workspaceId) return;
    const activeData = active.data.current as FolderDragData | undefined;
    const overData = over.data.current as
      | { type?: string; folderId?: string }
      | undefined;
    if (!activeData || activeData.type !== 'folder') return;
    if (!overData?.folderId) return;
    if (activeData.folderId === overData.folderId) return;

    const folderList: Folder[] = folders.data ?? [];
    const targetFolder = folderList.find((f) => f.id === overData.folderId) ?? null;
    const draggedFolder = folderList.find((f) => f.id === activeData.folderId) ?? null;

    const sameParent =
      targetFolder &&
      draggedFolder &&
      targetFolder.parent_id === draggedFolder.parent_id;

    if (sameParent && targetFolder) {
      reorderFolder.mutate(
        {
          id: activeData.folderId,
          sort_order: targetFolder.sort_order ?? 0,
          workspace_id: workspaceId,
        },
        {
          onSuccess: () => toast.success('Reordered'),
          onError: (e) => toast.error('Reorder failed', apiError(e)),
        },
      );
      return;
    }

    moveFolder.mutate(
      {
        id: activeData.folderId,
        parent_id: overData.folderId,
        workspace_id: workspaceId,
      },
      {
        onSuccess: () =>
          toast.success(
            `Moved ${draggedFolder?.name ?? 'folder'} into ${targetFolder?.name ?? 'folder'}`,
          ),
        onError: (e) => toast.error('Move failed', apiError(e)),
      },
    );
  };

  return (
    <AppShell crumbs={
      isStaff
        ? [{ label: 'Organizations', to: '/admin/organizations' }, { label: org.data?.name ?? orgId }]
        : [{ label: 'My files' }]
    }>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className={isClient ? 'flex flex-col gap-4' : 'grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4'}>
          {!isClient && (
            <Card title="Browse">
              {workspaces.isLoading || folders.isLoading ? (
                <div className="flex justify-center py-6">
                  <Spinner />
                </div>
              ) : !workspaceId ? (
                <EmptyState title="No workspace" description="This organization has no workspace yet." />
              ) : (
                <FolderTree
                  orgId={orgId}
                  folders={folders.data ?? []}
                  workspaceId={workspaceId}
                  showArchived={showArchived}
                  onToggleArchived={setShowArchived}
                  onCreateChild={(pid) => setCreatingParent(pid)}
                />
              )}
            </Card>
          )}

          <Card
            title="Top-level folders"
            actions={
              canManageFolders ? (
                <Button
                  variant="secondary"
                  onClick={() => setCreatingParent(null)}
                  disabled={!workspaceId}
                  className="text-xs"
                >
                  <FolderPlus size={14} /> New folder
                </Button>
              ) : null
            }
          >
            <p className="text-xs text-slate-500 mb-3">
              Folders organize files by topic. You can nest them up to any depth.
              To create a sub-folder, click the + icon next to any folder in the tree on the left.
            </p>
            {folders.isLoading ? (
              <Spinner />
            ) : rootFolders.length === 0 ? (
              <EmptyState
                title="No folders yet"
                description={
                  canManageFolders
                    ? 'Create a folder to start organizing files.'
                    : 'Your administrator will set up folders here.'
                }
                icon={<FolderPlus size={24} />}
                action={
                  canManageFolders ? (
                    <Button
                      variant="secondary"
                      onClick={() => setCreatingParent(null)}
                      disabled={!workspaceId}
                    >
                      <FolderPlus size={14} /> Create folder
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {rootFolders.map((f) => (
                  <li key={f.id}>
                    <Link
                      to={`/workspace/${orgId}/folders/${f.id}`}
                      className="block border border-[#E3E3F1] rounded-lg p-3 hover:border-[#A2A8FF] hover:bg-[#EFF0FF] transition-colors"
                    >
                      <div className="text-sm font-semibold text-[#12122C] truncate">{f.name}</div>
                      <div className="text-xs text-[#676687] mt-1 truncate">{f.path}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </DndContext>

      <Modal
        open={creatingParent !== undefined}
        onClose={() => {
          setCreatingParent(undefined);
          setNewName('');
        }}
        title="New folder"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setCreatingParent(undefined);
                setNewName('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submitCreate}
              loading={create.isPending}
              disabled={!newName.trim()}
              className="w-auto"
            >
              Create
            </Button>
          </>
        }
      >
        <Label htmlFor="folder-name">Name</Label>
        <Input
          id="folder-name"
          autoFocus
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="e.g. Invoices 2026"
        />
      </Modal>
    </AppShell>
  );
}
